import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Keyboard, StyleSheet } from 'react-native';
import { MBClearButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBAnimationBounceIn } from '@shared/components/animation';
import { MBIconV2 } from '@shared/components/image';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { TodoField } from '@shared/data/entity/fields/TodoField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { TodoItem } from '@shared/types/fieldValues';
import { i18n } from '@shared/util/i18n';
import { scrollActiveElementIntoView } from '@shared/util/scrollIntoView';
import { uuid } from '@shared/util/uuid';
import { UpdateField } from '../../types';
import { EditFieldContainer } from './EditFieldContainer';

interface TodoEditFieldProps {
  component: API.TodoComponent;
  field: TodoField | null;
  updateField: UpdateField;
  error?: string;
  allowClear?: boolean;
  style?: CSSProperties;
}

type PartialTodoItem = Partial<TodoItem> & { id: string };

export function TodoEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: TodoEditFieldProps) {
  const [todos, setTodos] = useState<TodoItem[]>(() => field?.todos ?? []);
  const [newTodo, setNewTodo] = useState<TodoItem>(createNewTodoItem);
  const existingTodos = useMemo(
    () => todos.filter(todo => todo.id !== newTodo.id),
    [todos, newTodo.id],
  );

  const { setComponentFocused, setComponentBlurred } = useComponentFocus();

  useEffect(() => {
    // update the field when todos change
    const updatedTodos = todos.map(trimTodoSummary);
    updateField(component.id, new TodoField(updatedTodos));
  }, [todos, component.id, updateField]);

  useEffect(() => {
    // Add or remove the new todo when it changes. If it's empty, remove it if
    // it was previously added. If it's not empty, add it or update it.
    setTodos(prevTodos => {
      const newTodoIsEmpty = isEmpty(newTodo.summary.trim());
      const newTodoIsIncluded = prevTodos.some(todo => todo.id === newTodo.id);
      if (newTodoIsIncluded && newTodoIsEmpty) {
        return prevTodos.filter(todo => todo.id !== newTodo.id);
      }
      if (newTodoIsIncluded && !newTodoIsEmpty) {
        return prevTodos.map(todo => (todo.id === newTodo.id ? newTodo : todo));
      }
      if (!newTodoIsIncluded && !newTodoIsEmpty) {
        return [...prevTodos, newTodo];
      }
      return prevTodos;
    });
  }, [newTodo]);

  const handleFocus = useCallback(() => {
    scrollActiveElementIntoView();
    setComponentFocused();
  }, [setComponentFocused]);

  const handleBlur = useCallback(() => {
    setComponentBlurred();
  }, [setComponentBlurred]);

  const updateTodo = useCallback((updatedItem: PartialTodoItem) => {
    setTodos(prevTodos => {
      return prevTodos.map(todo => {
        return todo.id === updatedItem.id ? { ...todo, ...updatedItem } : todo;
      });
    });
  }, []);

  const removeTodo = useCallback((id: string) => {
    setTodos(prevTodos => prevTodos.filter(todo => todo.id !== id));
  }, []);

  const updateNewTodo = useCallback((updatedItem: PartialTodoItem) => {
    setNewTodo(prev => ({ ...prev, ...updatedItem }));
  }, []);

  const clearNewTodo = useCallback(() => {
    setNewTodo(prev => ({ ...prev, summary: '' }));
  }, []);

  const resetNewTodo = useCallback(() => {
    setNewTodo(createNewTodoItem());
    // need to re-clear the new todo because react-native text input holds on
    // to its previous value
    requestAnimationFrame(() => setNewTodo(prev => ({ ...prev, summary: '' })));
  }, []);

  const onDone = useCallback(() => {
    const isNewTodoEmpty = isEmpty(newTodo.summary.trim());
    if (isNewTodoEmpty) {
      Keyboard?.dismiss();
    } else {
      resetNewTodo();
    }
  }, [newTodo.summary, resetNewTodo]);

  const onPaste = useCallback(
    (pastedText: string) => {
      const pastedTodos = getPastedTodos(pastedText);
      if (pastedTodos.length < 2) {
        return true;
      }
      setTodos(prevTodos => [...prevTodos, ...pastedTodos]);
      resetNewTodo();
      requestAnimationFrame(scrollActiveElementIntoView);
      return false;
    },
    [resetNewTodo],
  );

  const label = getComponentLabel(component, field?.todos ?? []);
  const placeholder = component.placeholder ?? i18n('card.blocks.todoInput.addPlaceholder');
  const hasItems = !isEmpty(field?.todos);
  const isReadOnly = !!component.readonly;
  if (isReadOnly && !hasItems) {
    return null;
  }

  const printContent =
    existingTodos.map(t => `[${isCompleted(t) ? 'x' : ' '}] ${t.summary}`).join('\n') || undefined;

  return (
    <EditFieldContainer
      component={component}
      label={label}
      showLabel={true}
      showIcon={false}
      error={error}
      style={style}
      printContent={printContent}
    >
      <View marginTop={0}>
        {existingTodos.map(todoItem => (
          <TodoItemField
            key={todoItem.id}
            todoItem={todoItem}
            updateTodo={updateTodo}
            removeTodo={removeTodo}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
            onDone={Keyboard?.dismiss}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={error}
            allowClear={allowClear}
          />
        ))}
        {!isReadOnly && (
          <TodoItemField
            isNew={true}
            todoItem={newTodo}
            updateTodo={updateNewTodo}
            removeTodo={clearNewTodo}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
            onPaste={onPaste}
            onDone={onDone}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={error}
            allowClear={allowClear}
          />
        )}
      </View>
    </EditFieldContainer>
  );
}

interface TodoItemFieldProps {
  todoItem: TodoItem;
  placeholder: string;
  isNew?: boolean;
  isReadOnly: boolean;
  canUpdateStatus?: boolean;
  updateTodo: (partialTodo: PartialTodoItem) => void;
  removeTodo: (id: string) => void;
  onPaste?: (pastedText: string) => void;
  onDone: VoidFunction;
  onFocus: VoidFunction;
  onBlur: VoidFunction;
  error?: string;
  allowClear?: boolean;
}

function TodoItemField({
  todoItem,
  placeholder,
  isNew = false,
  isReadOnly = false,
  updateTodo,
  removeTodo,
  onPaste,
  onDone,
  onFocus,
  onBlur,
  error,
  allowClear = true,
}: TodoItemFieldProps) {
  const colors = useContext(ColorThemeContext);
  const { id, summary } = todoItem;

  const setSummary = useCallback(
    (text: string) => updateTodo({ id, summary: text }),
    [id, updateTodo],
  );

  const markComplete = useCallback(() => {
    updateTodo({ id, status: 'COMPLETED' });
  }, [id, updateTodo]);

  const markIncomplete = useCallback(() => {
    updateTodo({ id, status: 'NEEDS-ACTION' });
  }, [id, updateTodo]);

  const onClear = useCallback(() => {
    removeTodo(id);
  }, [id, removeTodo]);

  const isComplete = !isNew && isCompleted(todoItem);
  const hasValue = !isEmpty(summary.trim());
  const showClearButton = hasValue && !isReadOnly && allowClear;

  return (
    <View row>
      {isNew && (
        <MBTouchableOpacity disabled={!hasValue} onPress={onDone}>
          <MBView center style={styles.todoIcon}>
            <MBIconV2
              name="plus-circle"
              size={grid(4.5)}
              color={!isEmpty(error) ? colors.error.foreground : colors.accent}
            />
          </MBView>
        </MBTouchableOpacity>
      )}
      {!isNew && isComplete && (
        <MBTouchableOpacity disabled={isReadOnly} onPress={markIncomplete}>
          <MBView center style={[styles.todoIcon, styles.todoIconComplete]}>
            <MBAnimationBounceIn>
              <MBIconV2 name="task-filled" size={grid(4.5)} color={colors.accent} />
            </MBAnimationBounceIn>
          </MBView>
        </MBTouchableOpacity>
      )}
      {!isNew && !isComplete && (
        <MBTouchableOpacity disabled={isReadOnly} onPress={markComplete}>
          <MBView center style={styles.todoIcon}>
            <MBIconV2 name="unfilled-circle" size={grid(4.5)} color={colors.accentInactive} />
          </MBView>
        </MBTouchableOpacity>
      )}
      <MBAutosizeTextInput
        value={summary}
        onChangeText={setSummary}
        returnKeyType="done"
        padding="none"
        blurOnSubmit={!isNew}
        placeholder={placeholder}
        color={isComplete ? colors.foregroundInactive : colors.foregroundActive}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={onPaste}
        onSubmitEditing={onDone}
        disabled={isReadOnly || isComplete}
        style={{
          flex: 1,
          alignSelf: 'center',
          marginTop: 0,
          marginBottom: 0,
          textDecorationLine: isComplete ? 'line-through' : 'none',
        }}
      />
      {showClearButton && <MBClearButton onPress={onClear} />}
    </View>
  );
}

function getComponentLabel(component: API.TodoComponent, todos: TodoItem[]): string | undefined {
  const { label } = component;
  if (isNil(label)) {
    return undefined;
  }

  const total = todos.length;
  if (total === 0) {
    return label;
  }

  const completed = todos.filter(isCompleted).length;
  return `${label} (${completed}/${total})`;
}

function createNewTodoItem(summary = ''): TodoItem {
  return {
    summary,
    status: 'NEEDS-ACTION',
    id: uuid(),
  };
}

function isCompleted(todoItem: TodoItem): boolean {
  return todoItem.status === 'COMPLETED';
}

function trimTodoSummary(todoItem: TodoItem): TodoItem {
  return { ...todoItem, summary: todoItem.summary.trim() };
}

function getPastedTodos(pastedText: string): TodoItem[] {
  const pastedLines = pastedText
    .split('\n')
    .map(line => line.trim())
    .filter(line => !!line);

  return pastedLines.map(createNewTodoItem);
}

const styles = StyleSheet.create({
  nativeInput: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    maxHeight: 100,
    marginTop: grid(0.75),
    marginBottom: grid(0.75),
  },
  todoIcon: {
    opacity: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: grid(0.25),
    width: grid(5.5),
    height: grid(5),
    paddingRight: 0,
  },
  todoIconComplete: {
    opacity: 0.7,
  },
});
